import { logError } from '../sentry';
import { throttle } from '../util';

const MAX_RELOAD_ATTEMPTS = 3;
export const HARD_RELOAD_KEY = 'klikkie:chunkHardReload';

// A chunk 404 (ChunkLoadError) almost always means the user holds a stale
// klikkie.js whose chunk-hash map points at files that have been replaced by
// a newer deploy. Forcing a hard reload makes the browser fetch fresh HTML
// → fresh /funnel/klikkie.js?v=<bumped> → fresh chunk map. Guarded by
// sessionStorage so we never reload more than once per tab.
export const isChunkLoadError = (error) =>
  Boolean(error)
  && (error.name === 'ChunkLoadError'
    || /Loading chunk \S+ failed/.test(error.message || ''));

export const shouldHardReload = (error, alreadyAttempted) =>
  isChunkLoadError(error) && !alreadyAttempted;

const loadChunkWithRetry = async (chunkPath, attempts = 0) => {
  try {
    const module = await import(`./${chunkPath}`);
    module.default();
  } catch (error) {
    console.warn('An error occurred while loading the page chunk:', error);
    if (attempts < MAX_RELOAD_ATTEMPTS) {
      console.warn(`Retrying to load the page chunk. Attempt number ${attempts + 1}`);
      await throttle(loadChunkWithRetry(chunkPath, attempts + 1), 300);
      return;
    }
    if (shouldHardReload(error, sessionStorage.getItem(HARD_RELOAD_KEY))) {
      sessionStorage.setItem(HARD_RELOAD_KEY, chunkPath);
      logError('Stale chunk detected, forcing hard reload to recover.', { chunkPath });
      window.location.reload();
      return;
    }
    logError(`Failed to load the page chunk after ${MAX_RELOAD_ATTEMPTS} attempts.`, {
      chunkPath,
      hardReloadAttempted: sessionStorage.getItem(HARD_RELOAD_KEY) || null,
    });
  }
};

// Strip language prefix and trailing slash (e.g. /de/subscribe/plan/ → /subscribe/plan)
const stripLangPrefix = (path) => {
  let p = path.replace(/\/$/, '') || '/';
  const match = p.match(/^\/(de|fr|es|nl)\//);
  return match ? p.slice(match[1].length + 1) : p;
};

export const loadPageScripts = async () => {
  const rawPathname = window.location.pathname;
  const pathname = stripLangPrefix(rawPathname);

  // KL-15093: photobook PDPs render discounted prices from the products API
  // at view time. Also matches PLP/category pages under the same prefix —
  // the chunk no-ops when the page has no size selector.
  if (
    pathname.startsWith('/products/photobooks/') ||
    pathname.startsWith('/producten/fotoboeken/')
  ) {
    return loadChunkWithRetry('photobooks/pdp');
  }

  switch (pathname) {
    case '/':
      return loadChunkWithRetry('homePage');
    case '/subscriptions':
    case '/abonnementen':
      return loadChunkWithRetry('subscriptionsPage');
    case '/faq':
      return loadChunkWithRetry('faqPage');
    case '/test-page.html':
      return loadChunkWithRetry('membership/checkout');
    case '/photobookgift':
    case '/fotoboekcadeau':
      return loadChunkWithRetry('photoBookGift');
    case '/photobook/signup':
    case '/fotoboek/aanmelden':
    case '/photobook/signup-for-discount-tt':
      return loadChunkWithRetry('photoBookSignup');
    case '/plans':
    case '/pakketten':
      return loadChunkWithRetry('plans');
    case '/photogift':
    case '/selectgift':
    case '/kiescadeau':
      return loadChunkWithRetry('createGift');
    case '/selectgift-travel':
    case '/selectgift-pet':
      return loadChunkWithRetry('selectGift');
    case '/schiphol-gift':
    case '/schiphol-cadeau':
      return loadChunkWithRetry('schiphol');
    case '/getapp':
    case '/downloadapp':
      return loadChunkWithRetry('getApp');
    case '/redeem/code':
    case '/activeer/code':
      return loadChunkWithRetry('redeem/code');
    case '/redeem/partner-gift':
    case '/activeer/partner-cadeau':
      return loadChunkWithRetry('redeem/partnerGift');
    case '/kpn':
      return loadChunkWithRetry('kpn/landing');
    case '/kpn/activeer':
    case '/kpn/verzilver':
      return loadChunkWithRetry('kpn/code');
    case '/essent/claim':
    case '/essent/verzilver':
      return loadChunkWithRetry('essent/code');
    case '/kpn/gefeliciteerd':
      return loadChunkWithRetry('kpn/thanks');
    case '/essent/congratulations':
    case '/essent/gefeliciteerd':
      return loadChunkWithRetry('essent/thanks');
    case '/redeem/discountcode':
    case '/redeem/congratulations':
    case '/activeer/gefeliciteerd':
      return loadChunkWithRetry('redeem/discountCode');
    case '/membership/start':
      return loadChunkWithRetry('membership/start');
    case '/membership/profile':
    case '/membership/profiel':
      return loadChunkWithRetry('membership/profile');
    case '/membership/login':
      return loadChunkWithRetry('membership/login');
    case '/membership/forgot':
    case '/membership/vergeten':
      return loadChunkWithRetry('membership/forgot');
    case '/membership/yourplan':
    case '/membership/jouwpakket':
      return loadChunkWithRetry('membership/yourPlan');
    case '/membership/test':
      return loadChunkWithRetry('membership/start');
    case '/membership/starterset':
    case '/membership/startersset':
      return loadChunkWithRetry('membership/starterSet');
    case '/membership/address':
    case '/membership/adres':
      return loadChunkWithRetry('membership/address');
    case '/membership/message':
    case '/membership/bericht':
      return loadChunkWithRetry('membership/message');
    case '/membership/checkout':
      return loadChunkWithRetry('membership/checkout');
    case '/membership/thanks':
    case '/membership/bedankt':
      return loadChunkWithRetry('membership/thanks');
    case '/membership/thanksdone':
      return loadChunkWithRetry('membership/thanksdone');
    case '/membership/download':
      return loadChunkWithRetry('membership/download');
    case '/membership/oops':
    case '/membership/oeps':
      return loadChunkWithRetry('membership/oops');
    case '/membership/load':
      return loadChunkWithRetry('membership/load');
    // Subscribe funnel (streamlined subscription checkout)
    case '/subscribe/plan':
      return loadChunkWithRetry('subscribe/plan');
    case '/subscribe/details':
      return loadChunkWithRetry('subscribe/details');
    case '/subscribe/checkout':
      return loadChunkWithRetry('subscribe/checkout');
    case '/subscribe/thanks':
      return loadChunkWithRetry('subscribe/thanks');
    default:
      return false;
  }
};
